"use client";

import { replaceBrowserUrlPreservingHistoryState } from "./browser-history";

export type LandingSectionId =
  | "platform"
  | "solutions"
  | "workflows"
  | "governance";

const LANDING_SECTION_NAVIGATION_KEY = "zom.landing.sectionTarget";

function isLandingSectionId(value: string): value is LandingSectionId {
  return (
    value === "platform" ||
    value === "solutions" ||
    value === "workflows" ||
    value === "governance"
  );
}

function normalizeLandingSectionId(value: string) {
  const sectionId = value.trim().replace(/^#/, "");
  return isLandingSectionId(sectionId) ? sectionId : null;
}

export function getLandingSectionIdFromHref(href: string) {
  if (!href) return null;

  if (href.startsWith("#")) {
    return normalizeLandingSectionId(href);
  }

  if (href.startsWith("/#")) {
    return normalizeLandingSectionId(href.slice(1));
  }

  try {
    const url = href.startsWith("http")
      ? new URL(href)
      : new URL(href, "https://zom.ai");

    if (url.pathname !== "/" && url.pathname !== "") {
      return null;
    }

    return normalizeLandingSectionId(url.hash);
  } catch {
    return null;
  }
}

export function queueLandingSectionNavigation(sectionId: LandingSectionId) {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(LANDING_SECTION_NAVIGATION_KEY, sectionId);
}

export function consumeLandingSectionNavigation() {
  if (typeof window === "undefined") return null;

  const sectionId = window.sessionStorage.getItem(
    LANDING_SECTION_NAVIGATION_KEY,
  );

  window.sessionStorage.removeItem(LANDING_SECTION_NAVIGATION_KEY);
  return sectionId && isLandingSectionId(sectionId) ? sectionId : null;
}

export function stripLandingSectionHash() {
  if (typeof window === "undefined") return null;

  const sectionId = getLandingSectionIdFromHref(window.location.href);
  if (!sectionId) return null;

  const url = new URL(window.location.href);
  url.hash = "";
  const cleanUrl = `${url.pathname}${url.search}`;
  replaceBrowserUrlPreservingHistoryState(cleanUrl || "/");

  return sectionId;
}

export function scrollToLandingSection(
  sectionId: LandingSectionId,
  behavior: ScrollBehavior = "smooth",
) {
  if (typeof window === "undefined") return false;

  const section = window.document.getElementById(sectionId);
  if (!section) return false;

  section.scrollIntoView({ behavior, block: "start" });
  return true;
}
